/**
 * Theme Utilities for Synap Marketing OS
 * Glassmorphic Design System
 */

export const glassVariants = {
  default: {
    background: 'bg-white/[0.04]',
    backdrop: '',
    border: 'border border-white/[0.06]',
    shadow: '',
  },
  strong: {
    background: 'bg-white/[0.06]',
    backdrop: '',
    border: 'border border-white/[0.08]',
    shadow: '',
  },
  subtle: {
    background: 'bg-white/[0.03]',
    backdrop: '',
    border: 'border border-white/[0.06]',
    shadow: '',
  },
} as const;

export type GlassVariant = keyof typeof glassVariants;

/**
 * Get glass effect classes for a specific variant
 */
export function getGlassClasses(variant: GlassVariant = 'default'): string {
  const config = glassVariants[variant];
  return `${config.background} ${config.backdrop} ${config.border} ${config.shadow}`;
}

/**
 * Brand color utilities optimized for glassmorphic design
 */
export const brandColors = {
  primary: {
    base: '#5DD4BF',
    light: '#7DE9D6',
    dark: '#3AB5A5',
    glow: 'shadow-[0_0_15px_rgba(93,212,191,0.5)]',
  },
  secondary: {
    base: '#4A90D6',
    light: '#75BDED',
    dark: '#2A5F9A',
    glow: 'shadow-[0_0_15px_rgba(74,144,214,0.5)]',
  },
  accent: {
    base: '#C8F44A',
    light: '#D4F75A',
    dark: '#A5CC3A',
    glow: 'shadow-[0_0_15px_rgba(200,244,74,0.5)]',
  },
  dark: {
    background: '#0A1F2E',
    surface: '#0F2533',
    elevated: '#122B38',
  },
} as const;

/**
 * Animation utilities for smooth transitions
 */
export const animations = {
  fadeIn: 'animate-fade-in',
  slideUp: 'animate-slide-up',
  slideDown: 'animate-slide-down',
  pulseSlow: 'animate-pulse-slow',
} as const;

/**
 * Spacing scale for consistent layout
 */
export const spacing = {
  xs: '0.5rem',   // 8px
  sm: '0.75rem',  // 12px
  md: '1rem',     // 16px
  lg: '1.5rem',   // 24px
  xl: '2rem',     // 32px
  '2xl': '3rem',  // 48px
  '3xl': '4rem',  // 64px
} as const;
